import { EXPLORER_BORDER_GRAY } from 'features/drilldown'
import { PlotMouseEvent } from 'plotly.js'
import CantileverTheme from 'styles/cantileverTheme'

import { HistoStackingGroup } from './configPanel/histoStackingTypes'
import { StackedHistogram } from './contexts/HistogramDataContext'
import { DrilldownPlotType } from './DrilldownHistogram'

export type PlotlyOrientation = 'h' | 'v'

type PlotlyTrace = {
  type: 'bar' | 'pie'
  orientation?: PlotlyOrientation
  name?: string
  x?: (number | string)[]
  y?: (number | string)[]
  labels?: string[]
  values?: number[]
  marker?: { color: string | string[] } | { colors: string | string[] }
}

type GeneratorType = {
  getPlotlyData: (
    histogram: StackedHistogram,
    orientation: 'v' | 'h',
    stackingGroups: ReadonlyArray<
      Pick<HistoStackingGroup, 'id' | 'name' | 'color'>
    >,
    selectedBars: ReadonlySet<SelectedBarKey>,
    baseColor: string
  ) => ReadonlyArray<PlotlyTrace>
  getPlotlyLayout: (
    title: string | undefined,
    orientation: PlotlyOrientation,
    numCategories: number
  ) => Partial<Plotly.Layout>
}

const DEFAULT_DISABLED_COLOR = CantileverTheme.colors.grayscale[400]

const MAX_HORIZONTAL_LABEL_LENGTH = 13

const BASE_PIE_COLORS = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#E57373',
  '#81C784',
  '#64B5F6',
  '#FFD54F',
  '#BA68C8',
  '#4DD0E1',
  '#F06292',
  '#9575CD',
  '#AED581',
  '#FFD740',
  '#90A4AE',
  '#A1887F',
  '#D4E157',
  '#FF7043',
  '#26A69A',
  '#8D6E63',
  '#BDBDBD',
  '#789262',
  '#00B8D4',
  '#C51162',
  '#FF6F00',
  '#00C853',
  '#D500F9',
  '#FF1744',
  '#00E5FF',
  '#76FF03',
  '#F50057',
  '#FFEA00',
  '#304FFE',
  '#C6FF00',
]

type SelectedBarKey = string
export const makeSelectedBarKey = (
  category: string,
  stackIdx: number | null
): SelectedBarKey => `${category}|${stackIdx}`

const getColors = (
  categories: ReadonlyArray<string>,
  stackIdx: number | null,
  stackColor: string,
  selectedBars: ReadonlySet<SelectedBarKey>,
  hasSelection: boolean
): string[] =>
  categories.map((category) => {
    const barKey = makeSelectedBarKey(category, stackIdx)
    const categoryKey = makeSelectedBarKey(category, null)
    const isSelected = selectedBars.has(barKey) || selectedBars.has(categoryKey)
    return !hasSelection || isSelected ? stackColor : DEFAULT_DISABLED_COLOR
  })

const BASE_PLOT_LAYOUT: Partial<Plotly.Layout> = {
  title: '',
  xaxis: {
    title: '',
    color: CantileverTheme.colors.gray[400],
  },
  yaxis: {
    title: '',
    color: CantileverTheme.colors.gray[400],
    ticklabelposition: 'inside top',
    tickfont: {
      color: CantileverTheme.colors.gray[400],
    },
  },
  plot_bgcolor: 'rgba(0, 0, 0, 0)',
  paper_bgcolor: 'rgba(0, 0, 0, 0)',
  autosize: true,
}

// For bar charts with a horizontal layout, we truncate the categories
// to stop the graph getting pushed over by its labels
const truncateCategories = (
  categories: ReadonlyArray<string>,
  orientation: PlotlyOrientation
): ReadonlyArray<string> =>
  orientation === 'h'
    ? categories.map((category) => {
        return category.length > MAX_HORIZONTAL_LABEL_LENGTH
          ? category.slice(0, MAX_HORIZONTAL_LABEL_LENGTH) + '...'
          : category
      })
    : categories

const getPlotlyLayout = (
  title: string | undefined,
  barmode: 'stack' | 'group'
): Partial<Plotly.Layout> => ({
  ...BASE_PLOT_LAYOUT,
  title,
  barmode,
})

function getPlotlyBarHeight(
  orientation: PlotlyOrientation,
  numRows: number
): number {
  return orientation === 'h' ? numRows * 35 + 200 : 700
}

function getPlotlyBarMargins(
  orientation: PlotlyOrientation
): Partial<Plotly.Margin> {
  return {
    l: 40,
    t: 30,
    r: 40,
    b: orientation === 'h' ? 60 : 100,
  }
}

function getPlotlyPieMargin(
  orientation: PlotlyOrientation
): Partial<Plotly.Margin> {
  return {
    l: 40,
    t: 30,
    r: 40,
    b: orientation === 'h' ? 60 : 100, // TODO (xdowns): this seems wrong
  }
}

/**
 * Generators to create Plotly charts from Drilldown histogram data.
 */
export const PLOTLY_GENERATORS: Record<DrilldownPlotType, GeneratorType> = {
  stacked: {
    getPlotlyData: (
      histogram,
      orientation,
      stackingGroups,
      selectedBars,
      baseColor
    ) => {
      const { sortedCategories, categoryToValues } = histogram
      const maxStacks = Math.max(
        ...sortedCategories.map((cat) => categoryToValues.get(cat)?.length ?? 0)
      )
      const stackIndices = Array.from({ length: maxStacks }, (_, i) => i)
      const hasSelection = selectedBars.size > 0

      return stackIndices.map((stackIdx) => {
        const traceValues = sortedCategories.map((category) => {
          const values = categoryToValues.get(category) ?? []
          return values[stackIdx] ?? 0
        })

        const group = stackingGroups[stackIdx]
        const colors = getColors(
          sortedCategories,
          stackIdx,
          group?.color ?? baseColor,
          selectedBars,
          hasSelection
        )

        const truncatedCategories = truncateCategories(
          sortedCategories,
          orientation
        )

        return {
          type: 'bar',
          orientation,
          name: group?.name ?? `Stack ${stackIdx + 1}`,
          x: [...(orientation === 'v' ? truncatedCategories : traceValues)],
          y: [...(orientation === 'v' ? traceValues : truncatedCategories)],
          marker: { color: colors },
          hovertext: sortedCategories,
          hoverinfo: 'text+x',
        }
      })
    },
    getPlotlyLayout: (title, orientation, numRows) => ({
      ...getPlotlyLayout(title, 'stack'),
      margin: getPlotlyBarMargins(orientation),
      height: getPlotlyBarHeight(orientation, numRows),
    }),
  },

  bar: {
    getPlotlyData: (
      histogram,
      orientation,
      _stackingGroups,
      selectedBars,
      baseColor
    ) => {
      const { sortedCategories, categoryToValues } = histogram
      const hasSelection = selectedBars.size > 0
      const traceValues = sortedCategories.map((category) => {
        const values = categoryToValues.get(category) ?? []
        return values.reduce((sum, val) => sum + val, 0)
      })

      const colors = getColors(
        sortedCategories,
        null,
        baseColor,
        selectedBars,
        hasSelection
      )

      const truncatedCategories = truncateCategories(
        sortedCategories,
        orientation
      )

      return [
        {
          type: 'bar',
          orientation,
          name: 'Total',
          marker: { color: colors },
          x: [...(orientation === 'v' ? truncatedCategories : traceValues)],
          y: [...(orientation === 'v' ? traceValues : truncatedCategories)],
          hovertext: sortedCategories,
          hoverinfo: 'text+x',
        },
      ]
    },
    getPlotlyLayout: (title, orientation, numRows) => ({
      ...getPlotlyLayout(title, 'group'),
      margin: getPlotlyBarMargins(orientation),
      height: getPlotlyBarHeight(orientation, numRows),
    }),
  },
  pie: {
    getPlotlyData: (histogram, _orientation, _stackingGroups, selectedBars) => {
      const { sortedCategories, categoryToValues } = histogram
      const hasSelection = selectedBars.size > 0

      const values = sortedCategories.map((category) => {
        const vals = categoryToValues.get(category) ?? []
        return vals.reduce((sum, val) => sum + val, 0)
      })

      const colors = sortedCategories.map((category, idx) => {
        const categoryKey = makeSelectedBarKey(category, null)
        const isSelected = selectedBars.has(categoryKey)
        const baseColor = BASE_PIE_COLORS[idx % BASE_PIE_COLORS.length]
        return !hasSelection || isSelected ? baseColor : DEFAULT_DISABLED_COLOR
      })

      return [
        {
          type: 'pie',
          labels: [...sortedCategories],
          values: [...values],
          marker: { colors },
        },
      ]
    },

    getPlotlyLayout: (title, orientation) => ({
      title,
      margin: getPlotlyPieMargin(orientation),
      legend: {
        font: {
          color: EXPLORER_BORDER_GRAY,
        },
      },
      height: 475,
    }),
  },
}

export function handlePlotlyClick(
  eventData: PlotMouseEvent,
  handleBarClick: (category: string, stackIdx: number | null) => void,
  traces: ReadonlyArray<PlotlyTrace>,
  stackEnabled: boolean,
  sortedCategories: ReadonlyArray<string>
) {
  const point = eventData.points[0]
  if (!point) return

  const trace = point.data
  const traceType = trace.type

  if (traceType === 'bar') {
    // For bar charts, orientation matters:
    // - Vertical ('v'): categories are on the x-axis
    // - Horizontal ('h'): categories are on the y-axis
    const categoryIndex = point.pointIndex
    const category = sortedCategories[categoryIndex]

    // Find the stackIdx by matching the clicked trace to the traces array
    const stackIdx = traces.findIndex((t) => t === trace)
    const resolvedStackIdx = stackIdx !== -1 && stackEnabled ? stackIdx : null

    handleBarClick(category, resolvedStackIdx)
  } else if (traceType === 'pie') {
    // For pie charts, use pointNumber to get the original category
    const category = sortedCategories[point.pointNumber]

    if (category) {
      handleBarClick(category, null)
    }
  }
}
